import React, { useContext, useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import styled from "styled-components";
import Layout from "../Lib/Layout";
import UserProvider from "../Lib/UserContext";
import {
  IconChartLine,
  IconScale,
  IconTrophy,
} from "@tabler/icons-react";
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  Stack,
  Textarea,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBulb,
  IconRocket,
} from "@tabler/icons-react";
import fetchApi from "../Lib/api";
import trackAiUsage from "../Lib/trackAiUsage";
import { useDisclosure } from "@mantine/hooks";
import QueryProvider from "../Lib/QueryClientProvider";
import useUserStore from "../../stores/userStore";
import { IconLayersDifference } from "@tabler/icons-react";
import { IconCashBanknote } from "@tabler/icons-react";
import SignUpModal from "../Authentication/SignUpModal";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

const Title = styled.h2`
  font-size: var(--font-size-h3);
  font-weight: var(--font-weight-body-2);
  color: var(--color-background-card);
  line-height: 26px;
`;

const Description = styled.p`
  margin-top: 4px;
  color: var(--color-background-main);
`;

function IdeaValidation({}) {
  const [idea, setIdea] = useState("");
  const [validateToken, setValidateToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [opened, { close, open }] = useDisclosure(false);
  const [ideaShort, setIdeaShort] = useState("");
  const [limitError, setLimitError] = useState("");
  const [checked, setChecked] = useState(true);
  const { user = null } = useUserStore((state) => state.user || {});
  const [evaluation, setEvaluation] = useState({});
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const { t } = useTranslation();
    const { i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(
      localStorage.getItem("appLanguage") || i18n.language || "en"
    );
  
    useEffect(() => {
      // Apply the language on component mount
      i18n.changeLanguage(selectedLanguage);
    }, [selectedLanguage, i18n]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSignUpModal(!user);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = async () => {
    if (idea.length < 20) {
      setIdeaShort(t("sense_check.errors.idea_too_short"));
      return;
    }
    try {
      setLoading(true);
      setLimitError("");
      await trackAiUsage("sense_check");
      const res = await fetchApi("/chat", "POST", {
        message: idea,
        temporary: !checked,
      });

      if (res.ok) {
        const responseData = await res.json();
        const { tokens = {}, ...evaluation } = responseData;
        console.log(evaluation, tokens);
        setValidateToken(tokens.startup_creation_token);
        setEvaluation(evaluation);
      } else {
        console.error("HTTP error! Failed to validate idea");
      }
    } catch (error) {
      if (error.code === "limit_exceeded") {
        setLimitError(error.message);
      } else {
        console.error("Error:", error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          onClose={close}
          size="lg"
          withCloseButton={false}
        >
          <div className="p-4 overflow-y-auto">
            <p className="mt-1 text-gray-8000">
              {t("sense_check.modal.complete_profile_message")}
            </p>
          </div>
          <div className="flex justify-end items-center gap-x-2 py-3 px-4">
            <Button onClick={close} variant="default" className="px-12">
              {t("sense_check.modal.cancel")}
            </Button>
            <Button
              href="/onboarding"
              component="a"
              variant="light"
              color="indigo"
              className="text-blue-700 px-12"
            >
              {t("sense_check.modal.complete_profile")}
            </Button>
          </div>
        </Modal>
      )}
      {showSignUpModal && (
        <Modal
          opened={showSignUpModal}
          transitionProps={{
            transition: "fade",
            duration: 600,
            timingFunction: "linear",
          }}
          withCloseButton={false}
          closeOnClickOutside={false}
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
        >
          <SignUpModal />
        </Modal>
      )}
      <main className="max-w-[900px] lg:pt-10 p-4 pt-6 mx-2  sm:mx-auto  space-x-2">
        <Anchor onClick={() => window.history.back()} target="_blank">
          <Group className="mb-2" spacing={4} h>
            <IconArrowLeft size={18} color="#1c7ed6" />
            {t("sense_check.main.go_back")}
          </Group>
        </Anchor>
        <Stack>
          <div>
            <Title>{t("sense_check.main.title")}</Title>
            <Description>{t("sense_check.main.description")}</Description>
          </div>
          <Stack>
            <div>
              <Alert
                icon={<IconAlertCircle size="1.5rem" />}
                variant="outline"
                color="green"
                py={4}
                style={{
                  maxWidth: "fit-content",
                  marginLeft: "auto",
                  marginBottom: "4px",
                }}
              >
                {t("sense_check.alert.ideal_score")}
              </Alert>

              <Textarea
                placeholder={t("sense_check.textarea.pitch_placeholder")}
                error={ideaShort}
                size="lg"
                withAsterisk
                minRows={10}
                style={{ outline: "none", border: "none" }}
                value={idea}
                onChange={(event) => {
                  if (ideaShort) setIdeaShort("");
                  setIdea(event.currentTarget.value.slice(0, 400));
                }}
              />

              <Group position="apart">
                <Checkbox
                  checked={checked}
                  onChange={(event) => setChecked(event.currentTarget.checked)}
                  label={t("sense_check.checkbox.terms")}
                />

                <div className="flex justify-between">
                  <p>({idea.length}/400)</p>
                </div>
              </Group>
              {limitError && (
                <Alert
                  icon={<IconAlertCircle size="1rem" />}
                  color="red"
                  mt="sm"
                >
                  {limitError || t("sense_check.errors.limit_reached")}
                </Alert>
              )}
            </div>
            <Group noWrap>
              <Stack spacing={2} className="w-full lg:w-1/3 ">
                <Button
                  onClick={onSubmit}
                  variant="default"
                  fullWidth
                  className="flex-grow-0 font-light rounded-lg border border-transparent bg-green-800 text-white hover:bg-green-600 disabled:opacity-50 disabled:pointer-events-none expert"
                  leftIcon={<IconBulb size="1.5rem" />}
                  loading={loading}
                  size="md"
                >
                  {t("sense_check.button.sense_check")}
                </Button>
              </Stack>
              <Button
                variant="default"
                fullWidth
                className="w-full lg:w-1/3 flex-grow-0 font-light border border-transparent bg-blue-600 text-white hover:bg-blue-600 disabled:opacity-50 disabled:pointer-events-none"
                leftIcon={<IconRocket size="1.5rem" />}
                size="md"
                component={user?.profile_complete ? "a" : "button"}
                href={user?.profile_complete ? "/projects/new" : undefined}
                onClick={!user?.profile_complete ? open : undefined}
              >
                {t("sense_check.button.create_project")}
              </Button>
            </Group>
          </Stack>
          <IdeaValidationSummary evaluation={evaluation} />
        </Stack>
      </main>
    </>
  );
}

const IdeaValidationSummary = ({ evaluation }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 mt-8">
      {/* Overall Score and Feedback Section */}
      <div className="flex justify-between items-start gap-6">
        {/* Feedback Section */}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-600">{t("sense_check.evaluation.feedback_title")}</h3>
          <div className="bg-gray-100 p-4 rounded-md space-y-4">
            <div>
              <strong className="text-gray-700">{t("sense_check.evaluation.strength_label")}:</strong>
              <p className="text-gray-500">
                {evaluation?.overall_feedback?.strength || t("sense_check.evaluation.strength_placeholder")}
              </p>
            </div>
            <div>
              <strong className="text-gray-700">{t("sense_check.evaluation.improvement_label")}:</strong>
              <p className="text-gray-500">
                {evaluation?.overall_feedback?.improvement || t("sense_check.evaluation.improvement_placeholder")}
              </p>
            </div>
            <div>
              <strong className="text-gray-700">{t("sense_check.evaluation.trends_label")}:</strong>
              <p className="text-gray-500">
                {evaluation?.overall_feedback?.reference_trends || t("sense_check.evaluation.trends_placeholder")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1">
          {/* Overall Score */}
          <div>
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-600">{t("sense_check.evaluation.score_title")}</h2>
              <span className="inline text-2xl font-bold text-indigo-600">
                {evaluation.final_score || "0/100"}
              </span>
            </div>
          </div>

          {/* Details Section */}
          <div className="space-y-4 flex-1 mt-4">
            <h2 className="text-lg font-semibold text-gray-600">{t("sense_check.evaluation.details_title")}</h2>

            {/* Market Demand */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconChartLine className="text-indigo-600 mr-3" size={24} />
                <span className="text-gray-700 text-lg">{t("sense_check.evaluation.market_demand")}</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{evaluation.market_demand || "0/20"}</span>
            </div>

            {/* Feasibility */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconScale className="text-indigo-600 mr-3" size={24} />
                <span className="text-gray-700 text-lg">{t("sense_check.evaluation.feasibility")}</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{evaluation.feasibility || "0/20"}</span>
            </div>

            {/* Scalability */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconLayersDifference className="text-indigo-600 mr-3" size={24} />
                <span className="text-gray-700 text-lg">{t("sense_check.evaluation.scalability")}</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{evaluation.scalability || "0/20"}</span>
            </div>

            {/* Competitive */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconTrophy className="text-indigo-600 mr-3" size={24} />
                <span className="text-gray-700 text-lg">{t("sense_check.evaluation.competitive_advantage")}</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{evaluation.competitive_advantage || "0/20"}</span>
            </div>

            {/* Revenue Model */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <IconCashBanknote className="text-indigo-600 mr-3" size={24} />
                <span className="text-gray-700 text-lg">{t("sense_check.evaluation.revenue_model")}</span>
              </div>
              <span className="text-lg font-bold text-indigo-600">{evaluation.revenue_model || "0/20"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AuthenticationPage() {
  return (
    <QueryProvider>
      <Layout>
        <UserProvider allowUnauthenticated>
          <Routes>
            <Route path="/ai_validation_tool" element={<IdeaValidation />} />
          </Routes>
        </UserProvider>
      </Layout>
    </QueryProvider>
  );
}
